import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { formatSeconds } from '../../utils/apneaTables';

/**
 * Countdown display for the fixed-duration phases — breathe-up, rest, and the
 * post-hold monitor. These count DOWN because the question they answer is "how
 * long until the next thing", unlike a hold.
 */

interface PhaseCounterProps {
  label: string;
  remainingSeconds: number;
  totalSeconds: number;
  /** Short instruction shown under the numeral. */
  cue?: string;
  /** What happens when this phase ends, so the user is never guessing. */
  nextLabel?: string;
  /** Optional content below the phase info, e.g. a link to further reading. */
  footnote?: React.ReactNode;
}

const PhaseCounter: React.FC<PhaseCounterProps> = ({
  label,
  remainingSeconds,
  totalSeconds,
  cue,
  nextLabel,
  footnote,
}) => {
  const progress = totalSeconds > 0
    ? Math.min(100, ((totalSeconds - remainingSeconds) / totalSeconds) * 100)
    : 0;

  return (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.15em' }}>
        {label}
      </Typography>

      <Typography
        variant="h2"
        sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}
      >
        {formatSeconds(remainingSeconds)}
      </Typography>

      {cue && (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {cue}
        </Typography>
      )}

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{ mt: 2, height: 6, borderRadius: 3 }}
      />

      {nextLabel && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          next: {nextLabel}
        </Typography>
      )}

      {footnote && <Box sx={{ mt: 1 }}>{footnote}</Box>}
    </Box>
  );
};

export default PhaseCounter;
